
import { useState } from 'react';
import { projects } from '../../../mocks/projects';

interface Project {
  id: number;
  title: string;
  client: string;
  category: string;
  year: string;
  duration: string;
  description: string;
  image: string;
  videoUrl: string;
}

export default function Projects() {
  const [activeVideo, setActiveVideo] = useState<Project | null>(null);

  return (
    <section id="work" className="py-24 px-6 bg-black">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-white">
            Selected Work
          </h2>
        </div>

        <div className="grid grid-cols-1 gap-8">
          {projects.map((project, index) => (
            <div
              key={project.id}
              className="group relative bg-gray-900 rounded-lg overflow-hidden hover:transform hover:scale-[1.02] transition-all duration-500 cursor-pointer"
              style={{ animation: `fadeInUp 0.6s ease-out ${index * 0.1}s both` }}
              onClick={() => setActiveVideo(project)}
            >
              {/* Project Image */}
              <div className="relative aspect-video overflow-hidden">
                <img
                  src={project.image}
                  alt={project.title}
                  className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent opacity-60 group-hover:opacity-40 transition-opacity duration-500"></div>

                {/* Play Button Overlay */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-16 h-16 flex items-center justify-center rounded-full bg-white/20 backdrop-blur-sm border border-white/40 group-hover:bg-teal-500/80 group-hover:border-teal-400 transition-all duration-300 scale-90 group-hover:scale-100">
                    <i className="ri-play-fill text-white text-2xl ml-1"></i>
                  </div>
                </div>
              </div>

              {/* Project Info */}
              <div className="p-8">
                <div className="flex items-center gap-3 mb-3">
                  <span className="text-sm font-medium text-teal-400">{project.category}</span>
                  <span className="text-gray-600">•</span>
                  <span className="text-sm text-gray-400">{project.year}</span>
                  <span className="text-gray-600">•</span>
                  <span className="text-sm text-gray-400">{project.duration}</span>
                </div>
                <h3 className="text-2xl font-bold text-white mb-2 group-hover:text-teal-400 transition-colors duration-300">
                  {project.title}
                </h3>
                <p className="text-gray-400 mb-4">{project.client}</p>
                <p className="text-gray-300 leading-relaxed">{project.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Video Modal */}
      {activeVideo && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-sm"
          onClick={() => setActiveVideo(null)}
        >
          <div
            className="relative w-full max-w-5xl mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              className="absolute -top-12 right-0 w-10 h-10 flex items-center justify-center text-white/70 hover:text-white transition-colors duration-200 cursor-pointer"
              onClick={() => setActiveVideo(null)}
            >
              <i className="ri-close-line text-2xl"></i>
            </button>

            {/* Video Title */}
            <div className="mb-4">
              <span className="text-teal-400 text-sm font-medium">{activeVideo.category} · {activeVideo.year}</span>
              <h3 className="text-white text-2xl font-bold mt-1">{activeVideo.title}</h3>
            </div>

            {/* YouTube Embed */}
            <div className="relative aspect-video rounded-lg overflow-hidden bg-black">
              <iframe
                src={activeVideo.videoUrl}
                title={activeVideo.title}
                className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
